'use client';

import React, {
  memo, useCallback, useEffect, useMemo, useRef
} from 'react';
import {
  Bar, BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis, YAxis
} from 'recharts';

import styles from '../explorer.module.scss';
import { QueryResult } from '../types';
import { formatNum } from '../utils/dataUtils';
import { computeProfile } from '../utils/statistics';


type Props = {
  columnName: string;
  result: QueryResult;
  anchorEl: HTMLElement;
  onClose: () => void;
};

function ProfilePopover( { columnName, result, anchorEl, onClose }: Props ) {
  const popoverRef = useRef<HTMLDivElement>( null );

  const profile = useMemo(
    () => computeProfile( columnName, result.rows ),
    [ columnName, result.rows ]
  );

  // Position the popover near the anchor element, clamped to viewport
  const position = useMemo( () => {
    // Guard against detached or unmounted DOM nodes
    if ( !anchorEl || !anchorEl.isConnected ) {
      return { top: 100, left: 100 };
    }

    const rect = anchorEl.getBoundingClientRect();
    const popoverWidth = 320;
    const popoverHeight = 400;
    let top = rect.bottom + 4;
    let left = rect.left;

    // Clamp to right edge
    if ( left + popoverWidth > window.innerWidth - 8 ) {
      left = window.innerWidth - popoverWidth - 8;
    }
    // Clamp to left edge
    if ( left < 8 ) left = 8;

    // If it would overflow bottom, show above the header instead
    if ( top + popoverHeight > window.innerHeight - 8 ) {
      top = rect.top - popoverHeight - 4;
      if ( top < 8 ) top = 8;
    }

    return { top, left };
  }, [ anchorEl ] );

  // Close on click-outside
  const handleClickOutside = useCallback( ( e: MouseEvent ) => {
    const target = e.target as HTMLElement;
    // Don't close if clicking a profile button — let the toggle in ResultsGrid handle it
    if ( target.closest( '[data-profile-btn]' ) ) return;
    if ( popoverRef.current && !popoverRef.current.contains( target ) ) {
      onClose();
    }
  }, [ onClose ] );

  // Close on Escape
  const handleKeyDown = useCallback( ( e: KeyboardEvent ) => {
    if ( e.key === 'Escape' ) {
      onClose();
    }
  }, [ onClose ] );

  useEffect( () => {
    document.addEventListener( 'mousedown', handleClickOutside );
    document.addEventListener( 'keydown', handleKeyDown );
    return () => {
      document.removeEventListener( 'mousedown', handleClickOutside );
      document.removeEventListener( 'keydown', handleKeyDown );
    };
  }, [ handleClickOutside, handleKeyDown ] );

  // Close on scroll of nearest scrollable ancestor or window resize
  useEffect( () => {
    if ( !anchorEl || !anchorEl.isConnected ) return;
    const scrollParent = anchorEl.closest( '.' + styles.resultsTableWrapper ) as HTMLElement | null;
    const handleDismiss = () => onClose();
    if ( scrollParent ) {
      scrollParent.addEventListener( 'scroll', handleDismiss );
    }
    window.addEventListener( 'resize', handleDismiss );
    return () => {
      if ( scrollParent ) {
        scrollParent.removeEventListener( 'scroll', handleDismiss );
      }
      window.removeEventListener( 'resize', handleDismiss );
    };
  }, [ anchorEl, onClose ] );

  return (
    <div
      ref={ popoverRef }
      className={ styles.profilePopover }
      data-testid="profile-popover"
      style={ { top: position.top, left: position.left } }
    >
      <div className={ styles.profilePopover__header }>
        <span className={ styles.profilePopover__title }>{ columnName }</span>
        <button type="button" data-testid="profile-close-btn" className={ styles.profilePopover__close } onClick={ onClose }>&times;</button>
      </div>
      <div className={ styles.profilePopover__body }>
        <table className={ styles.profilePopover__statsTable }>
          <tbody>
            <tr><td>Distinct</td><td>{ profile.distinctCount.toLocaleString() }</td></tr>
            <tr><td>Nulls</td><td>{ profile.nullCount.toLocaleString() } ({ profile.nullPercent.toFixed( 1 ) }%)</td></tr>
            { profile.min !== undefined && <tr><td>Min</td><td>{ typeof profile.min === 'number' ? formatNum( profile.min ) : String( profile.min ) }</td></tr> }
            { profile.max !== undefined && <tr><td>Max</td><td>{ typeof profile.max === 'number' ? formatNum( profile.max ) : String( profile.max ) }</td></tr> }
            { profile.mean !== undefined && <tr><td>Mean</td><td>{ formatNum( profile.mean ) }</td></tr> }
            { profile.stddev !== undefined && <tr><td title="Population standard deviation (σ)">Std Dev (σ)</td><td>{ formatNum( profile.stddev ) }</td></tr> }
          </tbody>
        </table>

        { profile.topValues.length > 0 && (
          <div className={ styles.profilePopover__section }>
            <div className={ styles.profilePopover__sectionTitle }>Top Values</div>
            <table className={ styles.profilePopover__statsTable }>
              <tbody>
                { profile.topValues.map( tv => (
                  <tr key={ tv.value }>
                    <td>{ tv.value }</td>
                    <td>{ tv.count }</td>
                  </tr>
                ) ) }
              </tbody>
            </table>
          </div>
        ) }

        { profile.histogram && (
          <div className={ styles.profilePopover__section }>
            <div className={ styles.profilePopover__sectionTitle }>Distribution</div>
            <div className={ styles.profilePopover__histogram }>
              <ResponsiveContainer width="100%" height={ 100 }>
                <BarChart data={ profile.histogram }>
                  <XAxis dataKey="bucket" tick={ false } />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={ { fontFamily: 'monospace', background: '#fff', fontSize: 11 } }
                  />
                  <Bar dataKey="count" fill="#0040FF" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) }
      </div>
    </div>
  );
}

export default memo( ProfilePopover );
